import React, { FC } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router'

import { Transport } from '@connectrpc/connect'
import { TransportProvider } from '@connectrpc/connect-query'
import { createConnectTransport } from '@connectrpc/connect-web'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { API_URL } from '@config'

import NotFoundPage from '@pages/NotFoundPage'
import HomePage from '@pages/HomePage'
import LoginPage from '@pages/LoginPage'
import Page from '@components/page'
import { Toaster } from '@components/ui/sonner'
import GoogleOAuthCallbackPage from '@pages/GoogleOAuthCallbackPage'
import { AuthType } from '@lib/auth-type'
import LivePage from '@pages/LivePage'
import CreatePage from '@pages/CreatePage'
import ProfilePage from '@pages/ProfilePage'
import SettingsPage from '@pages/SettingsPage'
import { SettingsView } from '@lib/views'

const queryClient = new QueryClient()

function useTransport(): Transport {
  return createConnectTransport({
    baseUrl: `${API_URL}/internal/connect`,
    fetch: (input, init) => fetch(input, { ...init, credentials: 'include' }),
  })
}

const App: FC = () => {
  const transport = useTransport()

  return (
    <TransportProvider transport={transport}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/google-oauth-callback"
              element={<GoogleOAuthCallbackPage />}
            />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/signup"
              element={<LoginPage authType={AuthType.SignUp} />}
            />

            <Route path="/" element={<Page />}>
              <Route index element={<HomePage />} />

              <Route path="create" element={<CreatePage />} />
              <Route path="live" element={<LivePage />} />

              <Route path="profile">
                <Route index element={<ProfilePage />} />
                <Route path=":username" element={<ProfilePage />} />
              </Route>

              <Route path="settings">
                <Route index element={<SettingsPage />} />
                <Route
                  path="content"
                  element={<SettingsPage view={SettingsView.ContentSettings} />}
                />
                <Route
                  path="privacy"
                  element={<SettingsPage view={SettingsView.PrivacySettings} />}
                />
              </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </QueryClientProvider>
    </TransportProvider>
  )
}

export default App
